import { Children, isValidElement, ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  useSidebar,
} from "@altimateai/lego";
import { cn } from "@/lib/utils";
import { IconChevronDown } from "@tabler/icons-react";

interface SidebarCollapsibleItemProps {
  label: string;
  icon?: ReactNode;
  children: ReactNode;
  activePaths?: string[];
  to?: string;
}

export const SidebarCollapsibleItem = ({
  label,
  icon,
  children,
  activePaths,
  to,
}: SidebarCollapsibleItemProps) => {
  const { pathname } = useLocation();
  const { state } = useSidebar();
  const isActive = activePaths?.some(path => pathname.startsWith(path));
  const [isOpen, setIsOpen] = useState(isActive);

  useEffect(() => {
    if (isActive) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [isActive]);

  if (state === "collapsed") {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            asChild={false}
            isActive={isActive}
            tooltip={label}
            className="[&>svg]:al-size-5 al-text-muted-foreground data-[active=true]:!al-text-brand"
          >
            {icon}
            <span className="group-data-[state=collapsed]:al-hidden">{label}</span>
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent side="right" align="start">
          {to && (
            <DropdownMenuItem asChild>
              <Link to={to} state={{ breadcrumbReset: true }}>
                {label}
              </Link>
            </DropdownMenuItem>
          )}
          {Children.map(children, child => {
            if (isValidElement(child)) {
              const { to, label } = child.props;
              return (
                <DropdownMenuItem asChild>
                  <Link to={to} state={{ breadcrumbReset: true }}>
                    {label}
                  </Link>
                </DropdownMenuItem>
              );
            }
            return null;
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild={!!to}
        isActive={isActive}
        tooltip={label}
        onClick={!to ? () => setIsOpen(!isOpen) : undefined}
        className={cn(
          "[&>svg]:al-size-5 al-text-muted-foreground data-[active=true]:!al-text-brand",
          state === "expanded" ? "al-p-3" : ""
        )}
      >
        {to ? (
          <Link to={to} state={{ breadcrumbReset: true }} onClick={e => e.stopPropagation()}>
            {icon}
            <span className="group-data-[state=collapsed]:al-hidden">{label}</span>
          </Link>
        ) : (
          <>
            {icon}
            <span className="group-data-[state=collapsed]:al-hidden">{label}</span>
          </>
        )}
      </SidebarMenuButton>
      {children && (
        <SidebarMenuAction
          onClick={() => setIsOpen(!isOpen)}
          className="group-data-[state=collapsed]:al-hidden"
        >
          <IconChevronDown className={cn("al-transition-transform", { "al-rotate-180": isOpen })} />
        </SidebarMenuAction>
      )}
      {isOpen && <SidebarMenuSub>{children}</SidebarMenuSub>}
    </SidebarMenuItem>
  );
};
